import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon } from "lucide-react";
import { Monogram } from "../components/Monogram";
import { CTABand } from "../components/CTABand";
import { MapleLeaf } from "../components/MapleLeaf";
import { SocialIcon } from "../components/SocialIcon";
import { ALL_LEADERS } from "../data/leadership";
import { IMAGES } from "../data/images";
import { EASE } from "../utils/motion";
export function LeaderProfile() {
  const {
    slug
  } = useParams();
  const reduce = useReducedMotion();
  const index = ALL_LEADERS.findIndex(l => l.slug === slug);
  if (index === -1) return <Navigate to="/about/leadership" replace data-id="element-1299" data-mp="35nvk73az2fv9" />;
  const leader = ALL_LEADERS[index];
  const prev = ALL_LEADERS[(index - 1 + ALL_LEADERS.length) % ALL_LEADERS.length];
  const next = ALL_LEADERS[(index + 1) % ALL_LEADERS.length];
  const tone = leader.role === "Principal" ? "red" : "navy";
  return <>
      <main className="bg-[#fbfaf6] pt-24" data-id="element-1300" data-mp="3b9z66g7nxjmu">
        <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-1301" data-mp="3g8u0ofezswms">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 py-4 text-xs text-[#6b7378]" data-id="element-1302" data-mp="3g67scdka2d3i">
            <Link to="/" className="hover:text-[#061f38]" data-id="element-1303" data-mp="36a4guh3jd23w">Home</Link>
            <ChevronRightIcon size={13} data-id="element-1304" data-mp="3689v50kfx5yr" />
            <Link to="/about/leadership" className="hover:text-[#061f38]" data-id="element-1305" data-mp="2sizntxuob82j">Leadership Team</Link>
            <ChevronRightIcon size={13} data-id="element-1306" data-mp="2x96b9udg8mj7" />
            <span className="text-[#061f38]" data-id="element-1307" data-mp="2xa7xmuzx0vg8">{leader.name}</span>
          </nav>
        </div>

        <section key={leader.slug} className="mx-auto grid max-w-[1440px] gap-10 overflow-hidden px-5 pb-24 pt-6 md:px-9 lg:grid-cols-12 lg:gap-16 lg:pb-32" data-id="element-1308" data-mp="2illcgcnlx9h3">
          <motion.div initial={{
          x: reduce ? 0 : "-110%",
          opacity: 0
        }} animate={{
          x: "0%",
          opacity: 1
        }} transition={{
          duration: reduce ? 0.01 : 0.9,
          ease: EASE
        }} className="lg:col-span-5" data-id="element-1309" data-mp="29ju7xb4sz8ot">
            <div className="lg:sticky lg:top-28" data-id="element-1310" data-mp="k4b2p6v1gugl">
              <Monogram initials={leader.initials} name={leader.name} image={leader.profileImage ?? leader.image} tone={tone} reveal={false} className="aspect-[4/5]" position="center 22%" data-id="element-1311" data-mp="fc9sdqhfldt7" />
              {leader.linkedin && <a href={leader.linkedin} target="_blank" rel="noreferrer" className="group mt-4 inline-flex items-center gap-3 bg-[#061f38] px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0a66c2]" data-id="element-1312" data-mp="5evenymw734l">
                  <SocialIcon network="linkedin" size={16} data-id="element-1313" data-mp="y0esmnyvqgn9" /> Connect on LinkedIn
                </a>}
            </div>
          </motion.div>

          <motion.div initial={{
          x: reduce ? 0 : 120,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} transition={{
          duration: reduce ? 0.01 : 0.9,
          delay: reduce ? 0 : 0.15,
          ease: EASE
        }} className="flex flex-col lg:col-span-6 lg:col-start-7 lg:pt-6" data-id="element-1314" data-mp="p2kwpe511p0m">
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1315" data-mp="1pb148592sbae"><MapleLeaf size={11} data-id="element-1316" data-mp="1tcmoibnxvrum" /> {leader.role.toUpperCase()}</p>
            <h1 className="mt-6 text-5xl font-medium leading-[0.92] tracking-[-0.07em] md:text-7xl" data-id="element-1317" data-mp="1yaxxkk6etybs">{leader.name}</h1>
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: reduce ? 0.01 : 0.8,
            delay: reduce ? 0 : 0.45,
            ease: EASE
          }} className="mt-10 h-px w-full origin-left bg-[#061f38]" data-id="element-1318" data-mp="231o2jwaxbz5y" />

            <p className="mt-10 text-2xl font-medium leading-snug tracking-[-0.03em]" data-id="element-1319" data-mp="2cy99woaf1mcy">{leader.summary}</p>

            <div className="mt-8 space-y-5 text-base leading-8 text-[#3d4b57] md:text-lg" data-id="element-1320" data-mp="2h2gy8kcyp0an">
              {leader.bio.map(p => <p key={p.slice(0, 32)} data-id="element-1321" data-mp="2lzh690tdgd4e">{p}</p>)}
            </div>

            {leader.quote && <blockquote className="my-12 border-l-2 border-[#c5233d] pl-6 text-3xl font-medium leading-tight tracking-[-0.045em] md:text-4xl" data-id="element-1322" data-mp="mozaoxck43fm">“{leader.quote}”</blockquote>}

            {leader.highlights && <div className="mt-8" data-id="element-1323" data-mp="3sj9ud2px487t">
                {leader.highlightsIntro && <p className="text-base leading-8 text-[#3d4b57] md:text-lg" data-id="element-1324" data-mp="3nhua3cvke5qh">{leader.highlightsIntro}</p>}
                <ul className="mt-5 border-t border-[#dedbd3]" data-id="element-1325" data-mp="69h34kv3hvzo">
                  {leader.highlights.map(h => <li key={h} className="flex items-start gap-4 border-b border-[#dedbd3] py-4 text-base" data-id="element-1326" data-mp="19b7ktlpfpcb">
                      <MapleLeaf size={13} className="mt-1.5 shrink-0 text-[#bb1e35]" data-id="element-1327" data-mp="34rw4r4j5iqqj" /> {h}
                    </li>)}
                </ul>
              </div>}

            {leader.closing && <div className="mt-8 space-y-5 text-base leading-8 text-[#3d4b57] md:text-lg" data-id="element-1328" data-mp="3epil7vg3mqcm">
                {leader.closing.map(p => <p key={p.slice(0, 32)} data-id="element-1329" data-mp="2qzfzry6owimz">{p}</p>)}
              </div>}

            <div className="mt-16 grid grid-cols-2 gap-px bg-[#dedbd3] pt-px" data-id="element-1330" data-mp="2m26aqjn7q8zo">
              <Link to={`/about/leadership/${prev.slug}`} className="group bg-[#fbfaf6] py-6 pr-4 transition-colors duration-200 hover:bg-[#f2f0eb]" data-id="element-1331" data-mp="11jm1z4kl4dst">
                <span className="flex items-center gap-2 text-xs text-[#6b7378]" data-id="element-1332" data-mp="16bn7m7n8z69v"><ArrowLeftIcon size={14} className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-1333" data-mp="rlobeaf964x1" /> Previous</span>
                <span className="mt-2 block text-lg font-medium tracking-[-0.03em]" data-id="element-1334" data-mp="wksld3a046of">{prev.name}</span>
              </Link>
              <Link to={`/about/leadership/${next.slug}`} className="group bg-[#fbfaf6] py-6 pl-4 text-right transition-colors duration-200 hover:bg-[#f2f0eb]" data-id="element-1335" data-mp="1k7qybqywwwhl">
                <span className="flex items-center justify-end gap-2 text-xs text-[#6b7378]" data-id="element-1336" data-mp="1b83zncxki4s8">Next <ArrowRightIcon size={14} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-1337" data-mp="1f9z8r3lqpmgk" /></span>
                <span className="mt-2 block text-lg font-medium tracking-[-0.03em]" data-id="element-1338" data-mp="3vyl8iwc7zg93">{next.name}</span>
              </Link>
            </div>
          </motion.div>
        </section>
      </main>
      <CTABand title="Meet the team in person on a campus visit." image={IMAGES.campus} data-id="element-1339" data-mp="26h7j5fz0ddxu" />
    </>;
}